import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MUNICIPAL_BANDS, type DayType, type LineSummaryRow } from './api';
import { formatPct, formatPopulation } from './summaryFormat';

type LoadState = 'loading' | 'error' | 'ready';

function formatNum(value: number | null, decimals = 1): string {
  if (value === null) return '—';
  return value.toFixed(decimals).replace('.', ',');
}

/** "Línea 500" → "500"; "503 — Corto UNS Palihue" → "503"; sin prefijo/guion reconocible, el label tal cual. */
function extractRouteAnchor(label: string): string {
  if (label.startsWith('Línea ')) return label.slice('Línea '.length);
  const dashIdx = label.indexOf(' — ');
  return dashIdx !== -1 ? label.slice(0, dashIdx) : label;
}

function variantDisplayLabel(label: string): string {
  const dashIdx = label.indexOf(' — ');
  return dashIdx !== -1 ? label.slice(dashIdx + 3) : label;
}

/**
 * Número de línea como ancla visual (18-20px, celeste, alto contraste) —
 * nunca repite la palabra "Línea" fila por fila. Microetiqueta debajo solo
 * cuando aporta (Circuito / Principal + variante), nunca decorativa.
 */
function RouteAnchorCell({ row, isVariant }: { row: LineSummaryRow; isVariant: boolean }): JSX.Element {
  if (isVariant) {
    return (
      <span className="db-route-variant">
        <span className="db-summary-variant-arrow">↳</span>
        {variantDisplayLabel(row.label)}
      </span>
    );
  }
  const microLabel =
    row.routeMode === 'CIRCUIT' ? null : (row.variants?.length ?? 0) > 0 ? 'Principal + variante' : null;
  return (
    <span className="db-route-anchor">
      <span className="db-route-anchor-number">{extractRouteAnchor(row.label)}</span>
      {row.routeMode === 'CIRCUIT' && <span className="db-badge db-badge-circuit">Circuito</span>}
      {microLabel && <span className="db-route-anchor-micro">{microLabel}</span>}
    </span>
  );
}

function StatusBadge({ row }: { row: LineSummaryRow }): JSX.Element {
  if (row.computeError) {
    return (
      <span className="db-badge db-badge-error" title={row.computeError}>
        Error
      </span>
    );
  }
  if (row.configSource === 'saved') {
    return <span className="db-badge db-badge-saved">Guardada</span>;
  }
  return <span className="db-badge db-badge-default">Estimación inicial</span>;
}

function fichaHref(analysisId: string, dayType: DayType, bandIndex: number): string {
  const band = MUNICIPAL_BANDS[bandIndex];
  return `/analysis/${analysisId}/ficha?dayType=${encodeURIComponent(dayType)}&band=${band.start}-${band.end}`;
}

/** Recorrido: CIRCUIT es un único camino — nunca IDA/VUELTA fingidos, una sola celda con colSpan. */
function RecorridoCells({ row }: { row: LineSummaryRow }): ReactNode {
  if (row.routeMode === 'CIRCUIT') {
    return (
      <td className="db-num-cell" colSpan={2}>
        Circuito · {formatNum(row.idaKm)}
      </td>
    );
  }
  return (
    <>
      <td className="db-num-cell">{formatNum(row.idaKm)}</td>
      <td className="db-num-cell">{formatNum(row.vueltaKm)}</td>
    </>
  );
}

/** Tiempo: mismo criterio que RecorridoCells — CIRCUIT muestra un único tiempo. */
function TiempoCells({ row }: { row: LineSummaryRow }): ReactNode {
  if (row.routeMode === 'CIRCUIT') {
    return (
      <td className="db-num-cell" colSpan={2}>
        {formatNum(row.idaTimeMin)}
      </td>
    );
  }
  return (
    <>
      <td className="db-num-cell">{formatNum(row.idaTimeMin)}</td>
      <td className="db-num-cell">{formatNum(row.vueltaTimeMin)}</td>
    </>
  );
}

/** DISCRETE_DEPARTURES no tiene frecuencia continua — cantidad de salidas fijas, horarios reales debajo, nunca "cada N min". */
function FrecuenciaCell({ row }: { row: LineSummaryRow }): JSX.Element {
  if (row.scheduleKind === 'DISCRETE_DEPARTURES' && row.discreteDepartureTimes) {
    const n = row.discreteDepartureTimes.length;
    return (
      <span className="db-coverage-cell">
        <strong>
          {n} {n === 1 ? 'salida/día' : 'salidas/día'}
        </strong>
        <span className="db-summary-muted">{row.discreteDepartureTimes.join(' · ')}</span>
      </span>
    );
  }
  return <>{row.frequencyMin !== null ? `cada ${row.frequencyMin} min` : '—'}</>;
}

function CoverageCell({
  coverage,
  lineTotal,
}: {
  coverage: LineSummaryRow['coverage2min'];
  lineTotal?: LineSummaryRow['coverage2min'];
}): JSX.Element {
  if (!coverage) return <span className="db-num-cell-inline">—</span>;
  return (
    <span className="db-coverage-cell db-coverage-cell-num">
      <strong>{formatPct(coverage.populationCoveragePct)}</strong>
      <span className="db-summary-muted">{formatPopulation(coverage.populationCovered)} hab.</span>
      {lineTotal && (
        <span
          className="db-summary-muted"
          title="Cobertura de la línea comercial completa (esta variante + sus otras variantes/rondines), contada una sola vez donde las áreas se superponen — no es la suma de cada una por separado."
        >
          Línea: {formatPct(lineTotal.populationCoveragePct)}
        </span>
      )}
    </span>
  );
}

function SummaryRow({
  row,
  dayType,
  bandIndex,
  onOpen,
  showStatusColumn,
  isVariant = false,
  expandable = false,
  expanded = false,
  onToggleExpand,
}: {
  row: LineSummaryRow;
  dayType: DayType;
  bandIndex: number;
  onOpen: (analysisId: string) => void;
  showStatusColumn: boolean;
  isVariant?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}): JSX.Element {
  return (
    <tr
      className={`db-summary-row${isVariant ? ' db-summary-row-variant' : ''}`}
      onClick={() => onOpen(row.analysisId)}
    >
      <td className="db-summary-line-cell">
        <span className="db-route-expand-slot">
          {expandable && (
            <button
              type="button"
              className="db-summary-expand-toggle"
              onClick={e => {
                e.stopPropagation();
                onToggleExpand?.();
              }}
              aria-expanded={expanded}
              aria-label={
                expanded ? `Contraer variantes de ${extractRouteAnchor(row.label)}` : `Ver variantes de ${extractRouteAnchor(row.label)}`
              }
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
        </span>
        <RouteAnchorCell row={row} isVariant={isVariant} />
      </td>
      {showStatusColumn && (
        <td>
          <StatusBadge row={row} />
        </td>
      )}
      <RecorridoCells row={row} />
      <TiempoCells row={row} />
      <td>
        <FrecuenciaCell row={row} />
      </td>
      <td className="db-num-cell">{formatNum(row.cycleMin)}</td>
      <td className="db-num-cell">{row.maxFleetRequired !== null ? row.maxFleetRequired : '—'}</td>
      <td className="db-num-cell">{row.fleetRequired !== null ? row.fleetRequired : '—'}</td>
      <td className="db-summary-muted">Pendiente asociación</td>
      <td className="db-num-cell">
        <CoverageCell coverage={row.coverage2min} lineTotal={row.lineCoverage2min ?? undefined} />
      </td>
      <td className="db-num-cell">
        <CoverageCell coverage={row.coverage5min} lineTotal={row.lineCoverage5min ?? undefined} />
      </td>
      <td>
        <Link
          to={fichaHref(row.analysisId, dayType, bandIndex)}
          className="db-ficha-link"
          onClick={e => e.stopPropagation()}
          target="_blank"
        >
          Ficha
        </Link>
      </td>
    </tr>
  );
}

interface SubColumn {
  label: string;
  /** Debe calzar exactamente con el `text-align` real de la celda de datos de esa columna (`.db-num-cell`/`.db-coverage-cell-num` son ambas `right`) — una sola fuente de verdad, nunca un offset manual aparte. */
  align: 'left' | 'right';
}

interface ColumnGroup {
  label: string;
  span: number;
  subColumns?: SubColumn[];
}

/** Tooltips breves solo donde el header corto puede confundir — "Unid. banda" vs "Máx. unid." son las dos formas de responder preguntas distintas ("¿cuántos coches en esta banda?" vs "¿cuántos como máximo en este día?"). */
const SUB_LABEL_TITLES: Record<string, string> = {
  'Unid. banda': 'Vehículos requeridos para la banda horaria seleccionada.',
  'Máx. unid.': 'Máximo de vehículos requeridos por la línea durante el día tipo seleccionado.',
};

const BASE_GROUPS: ColumnGroup[] = [
  {
    label: 'Recorrido',
    span: 2,
    subColumns: [
      { label: 'Ida · km', align: 'right' },
      { label: 'Vuelta · km', align: 'right' },
    ],
  },
  {
    label: 'Tiempo estimado',
    span: 2,
    subColumns: [
      { label: 'Ida · min', align: 'right' },
      { label: 'Vuelta · min', align: 'right' },
    ],
  },
  {
    label: 'Operación',
    span: 4,
    subColumns: [
      { label: 'Frecuencia', align: 'left' },
      { label: 'Ciclo · min', align: 'right' },
      { label: 'Máx. unid.', align: 'right' },
      { label: 'Unid. banda', align: 'right' },
    ],
  },
  { label: 'Paradas', span: 1, subColumns: [{ label: 'Asociadas', align: 'left' }] },
  {
    label: 'Cobertura',
    span: 2,
    subColumns: [
      { label: '2 min', align: 'right' },
      { label: '5 min', align: 'right' },
    ],
  },
  { label: 'Acción', span: 1, subColumns: [{ label: '', align: 'left' }] },
];

// "Ruta" (130px) tiene que alcanzar para el slot de chevron de 36px (área
// clickeable cómoda para expandir variantes) + el ancla numérica más larga
// ("513ex"/"519A") sin recortar.
const COL_WIDTHS_NO_STATUS = [130, 68, 68, 68, 68, 110, 62, 68, 68, 150, 108, 108, 66];
const COL_WIDTHS_WITH_STATUS = [130, 120, 68, 68, 68, 68, 110, 62, 68, 68, 150, 108, 108, 66];

function GroupedHeader({ showStatusColumn }: { showStatusColumn: boolean }): JSX.Element {
  const groups: ColumnGroup[] = showStatusColumn
    ? [{ label: 'Ruta', span: 1 }, { label: 'Estado', span: 1 }, ...BASE_GROUPS]
    : [{ label: 'Ruta', span: 1 }, ...BASE_GROUPS];

  return (
    <thead>
      <tr className="db-summary-thead-group">
        {groups.map(g => (
          <th key={g.label} colSpan={g.span} scope="colgroup">
            {g.label}
          </th>
        ))}
      </tr>
      <tr className="db-summary-thead-sub">
        <th />
        {showStatusColumn && <th />}
        {BASE_GROUPS.flatMap(g =>
          (g.subColumns ?? []).map(s => (
            <th
              key={`${g.label}-${s.label}`}
              title={SUB_LABEL_TITLES[s.label]}
              className={s.align === 'right' ? 'db-th-right' : undefined}
            >
              {s.label}
            </th>
          ))
        )}
      </tr>
    </thead>
  );
}

/**
 * Tabla resumen de las 17 líneas comerciales. Cada fila con variantes
 * (rondines/prolongaciones cerradas sin fila propia — ver
 * `LineSummaryRow.variants`) es expandible: no ensucia la tabla principal,
 * pero deja ver "Principal + Variante/Rondín" al abrirla. Header agrupado
 * de dos niveles + `table-layout: fixed` (`colgroup`) para que los títulos
 * largos no definan el ancho de columna.
 */
export function SummaryTable({
  rows,
  loadState,
  dayType,
  bandIndex,
  onOpen,
}: {
  rows: LineSummaryRow[];
  loadState: LoadState;
  dayType: DayType;
  bandIndex: number;
  onOpen: (analysisId: string) => void;
}): JSX.Element {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpand = (analysisId: string): void => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(analysisId)) next.delete(analysisId);
      else next.add(analysisId);
      return next;
    });
  };

  // Si las 17 líneas comparten el mismo estado de config, esa columna no
  // aporta información fila por fila — se resume una sola vez arriba de la
  // tabla en vez de repetirse 17 veces.
  const { showStatusColumn, uniformStatusLabel } = useMemo(() => {
    if (rows.length === 0) return { showStatusColumn: false, uniformStatusLabel: null };
    const first = rows[0];
    const uniform = rows.every(
      r => r.configSource === first.configSource && !r.computeError === !first.computeError
    );
    if (!uniform) return { showStatusColumn: true, uniformStatusLabel: null };
    const label = first.computeError ? 'Error' : first.configSource === 'saved' ? 'Guardada' : 'Estimación inicial';
    return { showStatusColumn: false, uniformStatusLabel: label };
  }, [rows]);

  const colWidths = showStatusColumn ? COL_WIDTHS_WITH_STATUS : COL_WIDTHS_NO_STATUS;

  return (
    <div className="db-summary-table-container">
      {uniformStatusLabel && (
        <p className="db-summary-uniform-status">
          {rows.length} líneas · {uniformStatusLabel}
        </p>
      )}
      <div className="db-summary-table-wrap">
        <table className="db-summary-table db-summary-table-fixed">
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={`col-${i}-${w}`} style={{ width: `${w}px` }} />
            ))}
          </colgroup>
          <GroupedHeader showStatusColumn={showStatusColumn} />
          <tbody>
            {loadState === 'loading' && rows.length === 0 && (
              <tr>
                <td colSpan={colWidths.length} className="db-summary-empty">
                  Cargando tabla resumen…
                </td>
              </tr>
            )}
            {rows.map(row => {
              const variants = row.variants ?? [];
              const hasVariants = variants.length > 0;
              const expanded = expandedIds.has(row.analysisId);
              return (
                <Fragment key={row.analysisId}>
                  <SummaryRow
                    row={row}
                    dayType={dayType}
                    bandIndex={bandIndex}
                    onOpen={onOpen}
                    showStatusColumn={showStatusColumn}
                    expandable={hasVariants}
                    expanded={expanded}
                    onToggleExpand={() => toggleExpand(row.analysisId)}
                  />
                  {hasVariants &&
                    expanded &&
                    variants.map(variant => (
                      <SummaryRow
                        key={variant.analysisId}
                        row={variant}
                        dayType={dayType}
                        bandIndex={bandIndex}
                        onOpen={onOpen}
                        showStatusColumn={showStatusColumn}
                        isVariant
                      />
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
